import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AppErrorBoundary from "./components/AppErrorBoundary";

// Loading spinner
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy-loaded pages (code splitting — each page loads only when visited)
const FoundersLandingPage = lazy(() => import("./pages/FoundersLandingPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const CardEditorPage = lazy(() => import("./pages/CardEditorPage"));
const PublicCardPage = lazy(() => import("./pages/PublicCardPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const BookingsPage = lazy(() => import("./pages/BookingsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PlaquettePage = lazy(() => import("./pages/PlaquettePage"));
const PlaquetteFoundersPage = lazy(() => import("./pages/PlaquetteFoundersPage"));
const LiaProPage = lazy(() => import("./pages/LiaProPage"));
const PhysicalCardPrintPage = lazy(() => import("./pages/PhysicalCardPrintPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Only Route (redirect to dashboard if logged in)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<FoundersLandingPage />} />
        <Route path="/old" element={<LandingPage />} />
        <Route path="/founders" element={<FoundersLandingPage />} />
        <Route path="/premium" element={<FoundersLandingPage />} />
        <Route path="/login" element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        } />
        <Route path="/register" element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        } />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Public Card Page */}
        <Route path="/u/:slug" element={<PublicCardPage />} />
        <Route path="/:slug" element={<PublicCardPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/offre" element={<InvitePage />} />
        <Route path="/go" element={<InvitePage />} />
        <Route path="/plaquette" element={<PlaquettePage />} />
        <Route path="/plaquette-founders" element={<PlaquetteFoundersPage />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/cards/new" element={
          <ProtectedRoute>
            <CardEditorPage />
          </ProtectedRoute>
        } />
        <Route path="/cards/:cardId" element={
          <ProtectedRoute>
            <CardEditorPage />
          </ProtectedRoute>
        } />
        <Route path="/cards/:cardId/print" element={
          <ProtectedRoute>
            <PhysicalCardPrintPage />
          </ProtectedRoute>
        } />
        <Route path="/cards/:cardId/analytics" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/cards/:cardId/leads" element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        } />
        <Route path="/team" element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />
        <Route path="/bookings" element={
          <ProtectedRoute>
            <BookingsPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/payment/success" element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        } />
        <Route path="/payment/cancel" element={<Navigate to="/pricing" replace />} />
        
        {/* Admin Panel */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        
        {/* Lia Pro - Business Assistant */}
        <Route path="/lia-pro" element={
          <ProtectedRoute>
            <LiaProPage />
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <div className="min-h-screen bg-background" translate="no">
            <div className="noise-overlay" />
            <AppRoutes />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(240 10% 6%)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'hsl(0 0% 98%)',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
